import Decimal from 'decimal.js';
/** Decimal canónico: signo opcional, dígitos, punto y decimales opcionales. */
const DECIMAL_STRING = /^-?\d+(\.\d+)?$/;
export class InvalidMoneyError extends Error {
    constructor(value) {
        super(`Monto inválido: ${JSON.stringify(value)} (se espera un string decimal como "1200000.50")`);
        this.name = 'InvalidMoneyError';
    }
}
/** Convierte a `Decimal` validando que sea un string decimal canónico (sin separadores de miles). */
export function toDecimal(value) {
    if (Decimal.isDecimal(value)) {
        if (!value.isFinite())
            throw new InvalidMoneyError(value.toString());
        return value;
    }
    if (typeof value !== 'string' || !DECIMAL_STRING.test(value.trim())) {
        throw new InvalidMoneyError(value);
    }
    return new Decimal(value.trim());
}
export function isMoneyValue(value) {
    if (Decimal.isDecimal(value))
        return value.isFinite();
    return typeof value === 'string' && DECIMAL_STRING.test(value.trim());
}
/** Agrupa dígitos de a tres con punto: `1200000` → `1.200.000`. */
export function groupThousands(digits) {
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}
/**
 * Formatea un monto en COP con convención es-CO: `$ 1.200.000`, `-$ 50.000`, `+$ 1.500,50`.
 * Redondeo de presentación: mitad hacia arriba (ROUND_HALF_UP) a los decimales mostrados.
 */
export function formatCop(value, options = {}) {
    const { decimals = 'auto', sign = 'auto', symbol = true } = options;
    const amount = toDecimal(value);
    const places = decimals === 'auto' ? (amount.isInteger() ? 0 : 2) : decimals;
    const rounded = amount.toDecimalPlaces(places, Decimal.ROUND_HALF_UP);
    const fixed = rounded.abs().toFixed(places);
    const [integerPart = '0', fraction] = fixed.split('.');
    const body = `${groupThousands(integerPart)}${fraction ? `,${fraction}` : ''}`;
    const prefix = symbol ? `$ ${body}` : body;
    if (rounded.isZero() || sign === 'never')
        return prefix;
    if (rounded.isNegative())
        return `-${prefix}`;
    return sign === 'always' ? `+${prefix}` : prefix;
}
/** Monto en palabras simples para lectores de pantalla: `1.200.000 pesos`. */
export function formatCopForSpeech(value) {
    const text = formatCop(value, { symbol: false });
    const negative = text.startsWith('-');
    const plain = negative ? text.slice(1) : text;
    return `${negative ? 'menos ' : ''}${plain} pesos`;
}
/**
 * Normaliza lo que la persona escribe en un campo de monto a un string decimal canónico.
 * `"$ 1.200.000"` → `"1200000"`; `"1.500,5"` → `"1500.5"` (con decimales); vacío → `""`.
 */
export function parseAmountInput(raw, options = {}) {
    const { allowDecimals = false, maxIntegerDigits = 13 } = options;
    const commaIndex = allowDecimals ? raw.indexOf(',') : -1;
    const integerRaw = commaIndex === -1 ? raw : raw.slice(0, commaIndex);
    const fractionRaw = commaIndex === -1 ? '' : raw.slice(commaIndex + 1);
    const integerDigits = integerRaw.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    const integer = integerDigits.slice(0, maxIntegerDigits);
    const fraction = fractionRaw.replace(/\D/g, '').slice(0, 2);
    if (integer === '' && commaIndex === -1)
        return '';
    const whole = integer === '' ? '0' : integer;
    return commaIndex === -1 ? whole : `${whole}.${fraction}`;
}
/**
 * Presenta un string decimal canónico para un campo editable (sin `$`): `"1200000"` → `"1.200.000"`.
 * Conserva una coma final o centavos parciales mientras la persona escribe (`"1500."` → `"1.500,"`).
 */
export function formatAmountInput(value) {
    if (value === '')
        return '';
    const [integer = '', fraction] = value.split('.');
    const grouped = groupThousands(integer);
    return fraction === undefined ? grouped : `${grouped},${fraction}`;
}
//# sourceMappingURL=money.js.map